import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import SaveButton from "./SaveButton";

/**
 * ReadAlongside — a compact, collapsible panel that surfaces the top-N
 * curriculum (KB01) chapters that match whatever the user is doing right
 * now.  Drops into every app workspace (Studio · Games · Website · Knuth ·
 * Kailash · Mendel) and Journey stops so learners never have to hunt for
 * the reading that pairs with the build.
 *
 * Props:
 *   seeds         — free-text search seeds (e.g. current prompt, stage name,
 *                   selected file, lesson title).  Required.
 *   eyebrow       — small label above the heading (default "Read alongside")
 *   title         — the heading (default "Chapters for this")
 *   askPrompt     — optional pre-filled question for Ms. Pradhan.  When
 *                   provided, a "Ask Ms. Pradhan" button appears.
 *   limit         — max articles (default 3)
 *   defaultOpen   — start expanded (default true).  Set false in tight
 *                   workspaces (e.g. Studio) so it stays a quiet ribbon.
 *   variant       — "panel" (bordered box, default) | "ribbon" (thin strip)
 *   testId        — data-testid override
 *   className     — extra wrapper classes
 */
export default function ReadAlongside({
  seeds,
  eyebrow = "Read alongside",
  title = "Chapters for this",
  askPrompt,
  limit = 3,
  defaultOpen = true,
  forceOpen = false,
  pinnedCode = null,
  variant = "panel",
  testId = "read-alongside",
  className = "",
}) {
  const [articles, setArticles] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const [userToggled, setUserToggled] = useState(false);

  // If the parent flips `forceOpen` to true after mount (e.g. "auto-open on
  // first prompt keystroke"), pop the panel open — but only when the user
  // hasn't manually collapsed it, so we never fight their intent.
  useEffect(() => {
    if (forceOpen && !userToggled) setOpen(true);
  }, [forceOpen, userToggled]);

  useEffect(() => {
    const q = (seeds || "").trim();
    if (!q && !pinnedCode) { setArticles([]); return; }
    let cancelled = false;
    // small debounce so a fast-typing prompt doesn't fire a request per keystroke
    const t = setTimeout(() => {
      api.post("/kb/search", { query: q, limit, book_code: "KB01", pinned_code: pinnedCode || null })
        .then(({ data }) => {
          if (!cancelled) setArticles(Array.isArray(data.articles) ? data.articles : []);
        })
        .catch(() => { if (!cancelled) setArticles([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [seeds, limit, pinnedCode]);

  const askPradhan = () => {
    if (!askPrompt) return;
    window.dispatchEvent(new CustomEvent("pradhan:open", { detail: { message: askPrompt } }));
  };

  if (articles === null || articles.length === 0) return null;

  const isRibbon = variant === "ribbon";
  const wrapCls = isRibbon
    ? "rounded-2xl border border-royal/30 bg-parchment/70"
    : "rounded-3xl border-2 border-royal/70 bg-parchment/70 shadow-[6px_6px_0_0_#2E1A47]";

  return (
    <section className={`${wrapCls} overflow-hidden ${className}`} data-testid={testId}>
      <header className={`flex items-start gap-3 ${isRibbon ? "px-4 py-2.5" : "px-5 pt-4 pb-3 border-b border-royal/15"} flex-wrap`}>
        <button
          type="button"
          onClick={() => { setOpen((o) => !o); setUserToggled(true); }}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          data-testid={`${testId}-toggle`}
        >
          <div className={`${isRibbon ? "w-7 h-7" : "w-9 h-9"} rounded-lg bg-gold/25 text-royal flex items-center justify-center flex-shrink-0`}>
            <BookOpen className={isRibbon ? "w-3.5 h-3.5" : "w-4 h-4"} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-gold">
              {eyebrow}
            </div>
            <h4 className={`font-heading font-semibold text-royal leading-tight ${isRibbon ? "text-sm" : "text-base"}`}>
              {title} <span className="text-royal/50 font-normal">· {articles.length}</span>
            </h4>
          </div>
          {open
            ? <ChevronUp className="w-4 h-4 text-royal/60" />
            : <ChevronDown className="w-4 h-4 text-royal/60" />}
        </button>
        {askPrompt && open && (
          <button
            type="button"
            onClick={askPradhan}
            className="btn-royal text-[11px] py-1.5 px-3 inline-flex items-center gap-1.5 flex-shrink-0"
            data-testid={`${testId}-ask`}
          >
            <MessageCircle className="w-3 h-3" /> Ask Ms. Pradhan
          </button>
        )}
      </header>
      {open && (
        <ul className="divide-y divide-royal/10">
          {articles.map((a) => (
            <li key={a.slug} className="flex items-start px-5 py-3 hover:bg-royal/[0.04] transition-colors group">
              <Link
                to={`/library/${a.slug}`}
                className="flex-1 flex items-start gap-3 min-w-0"
                data-testid={`${testId}-card-${a.code || a.slug}`}
              >
                <div className="pt-0.5 flex-shrink-0">
                  <div className="text-[10px] font-mono font-bold text-royal/70 whitespace-nowrap">
                    {a.module_code}·A{String(a.article_num || 0).padStart(2, "0")}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-sm font-semibold text-royal group-hover:text-marigold transition-colors leading-snug">
                      {a.title}
                    </span>
                    {a.pinned && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-royal bg-gold border border-royal rounded-full px-1.5 py-px whitespace-nowrap">
                        Ms. Pradhan's pick
                      </span>
                    )}
                  </div>
                  {a.summary && !isRibbon && (
                    <div className="text-[11px] text-ink2 mt-0.5 line-clamp-2 leading-relaxed">
                      {a.summary}
                    </div>
                  )}
                </div>
              </Link>
              <div className="pt-0.5 pl-3 flex items-center gap-2 flex-shrink-0">
                <SaveButton slug={a.slug} title={a.title} size="sm" testId={`${testId}-save-${a.code || a.slug}`} />
                <ArrowRight className="w-3.5 h-3.5 text-royal/40 group-hover:text-marigold group-hover:translate-x-0.5 transition-transform" />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
